import { useMemo } from "react";

import pick from "lodash-es/pick";
import { Layout } from "react-grid-layout";

import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import CardType from "@app/enum/dashboard/CardType";
import getCardByType from "@app/scene/dashboard/utils/getCardByType";
import { isMember } from "@app/store/iam/util";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import Dashboard from "@app/types/dashboard/Dashboard";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";
import loggingService from "@app/util/logging";

export interface GridCardInfo {
  configuration: CardConfiguration<any, any, any>;
  id: DashboardCard["id"];
  layout: Layout;
  metaData: Pick<DashboardCard, "createdAt" | "createdBy" | "updatedAt" | "updatedBy">;
  type: CardType;
  viewState: any;
}

const useGridCards = (boards: Dashboard[], scope: Scope, currentUser: User): GridCardInfo[] => {
  return useMemo(
    () => {
      const isTeamUserScope = !!scope.teamId && !!scope.userId;
      const needMergePreferences = isMember(currentUser) && isTeamUserScope;
      const needMergeGoal = isTeamUserScope;
      const needMerge = needMergePreferences || needMergeGoal;

      if (boards.length < 1) {
        return [];
      }
      if (boards.length !== 1 + (needMerge ? 1 : 0)) {
        loggingService.warning(
          `Unexpected number of boards returned, should be max of ${
            1 + (needMerge ? 1 : 0)
          } for the current scope`,
          { boards, scope }
        );
      }

      let cards = boards.find((board) => (isTeamUserScope ? board.userId === undefined : true))
        ?.settings.cards;
      if (!cards) {
        loggingService.error(`Unexpected boards returned, no suitable board found`, {
          boards,
          scope,
        });
        return [];
      }

      if (needMerge) {
        const overrides = boards.find(({ settings }) => (settings.overrides?.length ?? 0) > 0)
          ?.settings.overrides;
        if (overrides) {
          const overrideMap = new Map(
            overrides.map((override) => [override.id, override.configuration])
          );

          // We know that this code is compatible with cards of any configuration type
          // @ts-ignore
          cards = cards.map((card) => {
            if (!overrideMap.has(card.id)) {
              return card;
            }

            const overriddenConfig = overrideMap.get(card.id)!;

            return {
              ...card,
              configuration: {
                ...card.configuration,
                goal: needMergeGoal
                  ? !card.configuration.goal?.enabled
                    ? // if goal is completely disabled, take the overridden config
                      { ...overriddenConfig.goal, personal: true }
                    : // otherwise, use the parent card goal
                      card.configuration.goal
                  : card.configuration.goal,
                preferences: needMergePreferences
                  ? overriddenConfig.preferences ?? card.configuration.preferences
                  : card.configuration.preferences,
              },
            };
          });
        }
      }

      return cards!
        .map((dashboardCard) => {
          const cardDescriptor = getCardByType(dashboardCard.type);
          if (!cardDescriptor) {
            return undefined;
          }

          // TODO: add view state, probably reading it from the store?
          const viewState = {
            fullWidth: dashboardCard.fullWidth,
          };

          const { h, w } = cardDescriptor.getCardDimensions({
            // TODO: think about how to avoid ts-ignore
            // this is safe since dashboardCard.configuration is exactly for the dashboardCard type which we used to get cardDescriptor
            // @ts-ignore
            configuration: dashboardCard.configuration,
            scope,
            viewState,
          });

          const layout: GridCardInfo["layout"] = {
            h,
            i: String(dashboardCard.id),
            isResizable: false,
            w,
            x: dashboardCard.x,
            y: dashboardCard.y,
          };

          return {
            id: dashboardCard.id,
            configuration: dashboardCard.configuration,
            layout,
            metaData: pick(dashboardCard, ["createdAt", "createdBy", "updatedAt", "updatedBy"]),
            type: dashboardCard.type,
            viewState,
          };
        })
        .filter(isDefined);
    },
    // Excluding scope from here because when scope changes, boards changes, so we're safe
    // And useMemo is not triggered right after scope change till the new boards are loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boards, currentUser]
  );
};

export default useGridCards;
